import Phaser from 'phaser';
import { Entity, EntityConfig } from './base/Entity';
import { EightDirectionBehavior, EightDirectionConfig } from './behaviors/EightDirectionBehavior';
import { SineBehavior, SineConfig } from './behaviors/SineBehavior';
import { RuntimeManagerWeapon, RuntimeManagerWeaponState } from '../weapons/RuntimeManagerWeapon';
import { audioManager } from '../core/CoreManagerAudio';
import { Allegiance, LayerDepths } from '../constants/enums';

export interface PlayerConfig extends EntityConfig {
  // Movement config (passed to EightDirectionBehavior)
  maxSpeed?: number;
  acceleration?: number;
  deceleration?: number;

  // Sine behaviors config (like C3)
  sineVertical?: SineConfig | false;  // false to disable
  sineHorizontal?: SineConfig | false; // false to disable

  // Weapon config
  enableWeapons?: boolean;  // default true
}

/**
 * Player - The UFO controlled by the player
 *
 * Based on Construct 3 player configuration:
 * - 8Direction behavior for movement
 * - BoundToLayout to stay within level bounds
 * - Sine behaviors for hover effect
 *
 * Instance variables from C3:
 * - estado: "Vivo" | "Muerto"
 * - health: number
 * - isFrezze: boolean
 * - isAbducting: boolean
 */
export class Player extends Entity {
  // State
  public estado: 'Vivo' | 'Muerto' = 'Vivo';
  public health: number = 15;
  public maxHealth: number = 15;
  public isFrozen: boolean = false;
  public isAbducting: boolean = false;
  public isPlayingPowerup: boolean = false;
  private debugInvulnerable: boolean = false;

  // Controla si el clamp al mundo esta activo para salidas tipo "Cuphead" termina y se mueve solo el ufo
  private worldClampEnabled: boolean = true;

  // Behaviors
  private eightDirection!: EightDirectionBehavior;
  private sineVertical?: SineBehavior;
  private sineHorizontal?: SineBehavior;

  // Weapons
  public weapons?: RuntimeManagerWeapon;

  constructor(scene: Phaser.Scene, x: number, y: number, config: PlayerConfig = {}) {
    super(scene, x, y, 'player', {
      ...config,
      allegiance: Allegiance.Player,
    });

    // Check if player is loaded as atlas or static image
    const texture = scene.textures.get('player');
    const frames = texture?.getFrameNames() ?? [];
    const isAtlas = frames.length > 0;

    if (isAtlas) {
      // Atlas mode: set initial frame
      if (frames.includes('player_default_0')) {
        this.setFrame('player_default_0');
      } else {
        this.setFrame(frames[0]);
      }
      // console.log('[Player] Loaded as atlas with', frames.length, 'frames');
    } else {
      // Static image mode: no animations available
      // console.log('[Player] Loaded as static image (no animations)');
    }

    // Scale to match C3 size (original: 824x453, C3 game size: 136x67)
    // Scale factor: 136/824 ≈ 0.165
    this.setScale(0.165);

    // Initialize health
    this.health = config.hp ?? 15;
    this.maxHealth = config.maxHp ?? this.health;

    // Setup physics body
    this.setupPhysics();

    // Add movement and sine behaviors
    this.setupBehaviors(config);

    // Setup weapon system
    if (config.enableWeapons !== false) {
      this.setupWeapons();
    }

    // Set depth above most entities
    this.setDepth(LayerDepths.Player);

    // Enable animation listeners for image points (player is always visible)
    this.onEnterView();

    // Start with default animation
    this.scene.time.delayedCall(0, () => {
      if (this.scene.anims.exists('player_Default')) {
        this.play('player_Default');
      }
    });
  }

  /**
   * Configure physics body
   */
  private setupPhysics(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // No gravity for UFO
    body.setAllowGravity(false);

    // Hitbox size as percentage of original sprite (before scale)
    // Original sprite: 824x453, scaled to 0.165 = ~136x75 display
    // setSize() works in original sprite coordinates
    const hitboxWidthRatio = 0.85;
    const hitboxHeightRatio = 0.75;

    const hitboxWidth = this.width * hitboxWidthRatio;
    const hitboxHeight = this.height * hitboxHeightRatio;

    // Center the hitbox on the sprite
    const offsetX = (this.width - hitboxWidth) / 2;
    const offsetY = (this.height - hitboxHeight) / 2;

    body.setSize(hitboxWidth, hitboxHeight);
    body.setOffset(offsetX, offsetY);

    // Don't go outside world bounds
    body.setCollideWorldBounds(true);
  }

  /**
   * Add behaviors to player
   */
  private setupBehaviors(config: PlayerConfig): void {
    // 8 Direction movement (C3 config)
    const eightDirConfig: EightDirectionConfig = {
      maxSpeed: config.maxSpeed ?? 800,
      acceleration: config.acceleration ?? 1200,
      deceleration: config.deceleration ?? 1000,
      directions: 8,
      setAngle: false,
      defaultControls: true,
    };

    this.eightDirection = new EightDirectionBehavior(this, eightDirConfig);
    this.addBehavior(this.eightDirection);

    // Sine behavior - Vertical hover (C3: Sine)
    if (config.sineVertical !== false) {
      const verticalConfig: SineConfig = {
        movement: 'vertical',
        wave: 'sine',
        period: 1,      // C3 default
        magnitude: 3,   // C3 default
        ...config.sineVertical,
      };
      this.sineVertical = new SineBehavior(this, verticalConfig);
      this.addBehavior(this.sineVertical);
    }

    // Sine behavior - Horizontal hover (C3: Sine2)
    if (config.sineHorizontal !== false) {
      const horizontalConfig: SineConfig = {
        movement: 'horizontal',
        wave: 'sine',
        period: 2,      // C3 default
        magnitude: -4,  // C3 default (negative = opposite direction)
        ...config.sineHorizontal,
      };
      this.sineHorizontal = new SineBehavior(this, horizontalConfig);
      this.addBehavior(this.sineHorizontal);
    }
  }

  /**
   * Setup weapon system
   */
  private setupWeapons(): void {
    this.weapons = new RuntimeManagerWeapon({
      scene: this.scene,
      getPlayerPosition: () => ({ x: this.x, y: this.y }),
      getImagePoint: (pointName: string) => this.getImagePoint(pointName),
    });

    // Listen for tractor beam events
    this.scene.events.on('tractorBeamStarted', this.onTractorBeamStarted, this);
    this.scene.events.on('tractorBeamStopped', this.onTractorBeamStopped, this);
  }

  /**
   * Handle tractor beam started
   */
  private onTractorBeamStarted(): void {
    this.startAbducting();
  }

  /**
   * Handle tractor beam stopped
   */
  private onTractorBeamStopped(): void {
    this.stopAbducting();
  }

  /**
   * Update player each frame
   */
  update(time: number, delta: number): void {
    // Don't update if frozen or dead
    if (this.isFrozen || this.estado === 'Muerto') {
      this.eightDirection.stop();
      return;
    }

    // Update all behaviors (EightDirection + Sine behaviors)
    super.update(time, delta);

    // Update weapon system
    if (this.weapons) {
      this.weapons.update(time, delta);
    }

    // Clamp position to world bounds (prevents tunneling at high sprint speeds)
    if (this.worldClampEnabled) {
      this.clampToWorldBounds();
    }
  }

  /**
   * Clamp player position to world bounds (prevents escaping at high speeds)
   */
  private clampToWorldBounds(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const bounds = this.scene.physics.world.bounds;
    const halfWidth = body.width / 2;
    const halfHeight = body.height / 2;

    // Clamp X
    if (this.x - halfWidth < bounds.x) {
      this.x = bounds.x + halfWidth;
      body.velocity.x = 0;
    } else if (this.x + halfWidth > bounds.right) {
      this.x = bounds.right - halfWidth;
      body.velocity.x = 0;
    }

    // Clamp Y
    if (this.y - halfHeight < bounds.y) {
      this.y = bounds.y + halfHeight;
      body.velocity.y = 0;
    } else if (this.y + halfHeight > bounds.bottom) {
      this.y = bounds.bottom - halfHeight;
      body.velocity.y = 0;
    }
  }

  /**
   * Take damage
   */
  takeDamage(amount: number): void {
    if (this.estado === 'Muerto' || this.debugInvulnerable) return;

    this.health -= amount;

    // Emit damage event
    this.scene.events.emit('playerDamaged', {
      player: this,
      damage: amount,
      health: this.health,
    });

    if (this.health <= 0) {
      this.die();
    }
  }

  /**
   * Heal the player
   */
  heal(amount: number): void {
    this.health = Math.min(this.health + amount, this.maxHealth);

    this.scene.events.emit('playerHealed', {
      player: this,
      amount: amount,
      health: this.health,
    });
  }

  /**
   * Player death
   */
  die(): void {
    if (this.debugInvulnerable) return;
    if (this.estado === 'Muerto') return; // Prevent multiple calls

    this.estado = 'Muerto';
    this.health = 0;
    this.eightDirection.stop();

    // Disable physics
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.setVelocity(0, 0);
      body.setEnable(false);
    }

    // Play death animation sequence
    this.playDeathSequence();

    // Emit death event
    this.scene.events.emit('playerDied', { player: this });
  }

  /**
   * Play death animation sequence: flash → shake → explosion → fade
   */
  private playDeathSequence(): void {
    const scene = this.scene;

    // 1. Flash effect (red/white tint 3 times)
    let flashCount = 0;
    const flashInterval = scene.time.addEvent({
      delay: 80,
      repeat: 5,
      callback: () => {
        if (flashCount % 2 === 0) {
          this.setTint(0xff0000); // Red
        } else {
          this.clearTint(); // Normal
        }
        flashCount++;
      },
    });

    // 2. After flash, shake camera and spawn explosion
    scene.time.delayedCall(500, () => {
      // Camera shake
      scene.cameras.main.shake(300, 0.02);

      // Play explosion sound
      audioManager.playSfx('sfx_explosion_big', 'explosion', scene);

      // 3. Spawn explosion sprite at player position
      const explosion = scene.add.image(this.x, this.y, 'playerexplotion');
      explosion.setDepth(this.depth + 1);
      explosion.setScale(1.5); // Make it bigger
      explosion.setTint(0x6688ff); // Blue-ish tint
      explosion.setBlendMode(Phaser.BlendModes.ADD); // Make black transparent

      // Explosion grows and fades
      scene.tweens.add({
        targets: explosion,
        scale: 2.5,
        alpha: 0,
        duration: 600,
        ease: 'Power2',
        onComplete: () => {
          explosion.destroy();
        },
      });

      // 4. Fade out the player sprite
      scene.tweens.add({
        targets: this,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
        onComplete: () => {
          this.setVisible(false);
          // Emit animation complete event
          scene.events.emit('playerDeathComplete', { player: this });
        },
      });
    });
  }

  /**
   * Freeze the player (can't move)
   */
  freeze(): void {
    this.isFrozen = true;
    this.eightDirection.stop();
  }

  /**
   * Unfreeze the player
   */
  unfreeze(): void {
    this.isFrozen = false;
  }

  /**
   * Start abducting - play Eating animation
   */
  startAbducting(): void {
    this.isAbducting = true;
    // Play eating animation only if powerup animation is not playing
    if (!this.isPlayingPowerup && this.scene.anims.exists('player_Eating')) {
      this.play('player_Eating', true);
    }
  }

  /**
   * Stop abducting - return to Default animation
   */
  stopAbducting(): void {
    this.isAbducting = false;
    // Return to default animation only if powerup is not playing
    if (!this.isPlayingPowerup && this.scene.anims.exists('player_Default')) {
      this.play('player_Default', true);
    }
  }

  /**
   * Play powerup animation when entity is absorbed
   * Blocks eating/default animations until complete
   */
  playPowerupAnimation(): void {
    if (!this.scene.anims.exists('player_Powerup')) return;

    this.isPlayingPowerup = true;
    this.play('player_Powerup', true);

    // Return to appropriate animation after powerup finishes
    this.once('animationcomplete', (anim: Phaser.Animations.Animation) => {
      if (anim.key === 'player_Powerup') {
        this.isPlayingPowerup = false;
        // Check if still abducting
        if (this.isAbducting && this.scene.anims.exists('player_Eating')) {
          this.play('player_Eating', true);
        } else if (this.scene.anims.exists('player_Default')) {
          this.play('player_Default', true);
        }
      }
    });
  }

  /**
   * Get the movement behavior for external control
   */
  getMovementBehavior(): EightDirectionBehavior {
    return this.eightDirection;
  }

  /**
   * Activa/desactiva el clamp al mundo (usar para la salida de nivel)
   */
  setWorldClampEnabled(enabled: boolean): void {
    this.worldClampEnabled = enabled;
  }

  /**
   * Check if player is sprinting (Shift held)
   */
  isSprinting(): boolean {
    return this.eightDirection.isSprinting();
  }

  /**
   * Check if player is alive
   */
  isAlive(): boolean {
    return this.estado === 'Vivo';
  }

  setDebugInvulnerable(enabled: boolean): void {
    this.debugInvulnerable = enabled;
  }

  /**
   * Reset player state
   */
  resetPlayer(x: number, y: number): void {
    this.setPosition(x, y);
    this.health = this.maxHealth;
    this.estado = 'Vivo';
    this.isFrozen = false;
    this.isAbducting = false;
    this.eightDirection.stop();
    this.setActive(true);
    this.setVisible(true);

    // Re-enable weapons
    if (this.weapons) {
      this.weapons.setEnabled(true);
    }
  }

  /**
   * Get weapon state for saving
   */
  getWeaponState(): RuntimeManagerWeaponState | undefined {
    return this.weapons?.getState();
  }

  /**
   * Load weapon state
   */
  loadWeaponState(state: RuntimeManagerWeaponState): void {
    this.weapons?.loadState(state);
  }

  /**
   * Unlock a weapon
   */
  unlockWeapon(weaponId: string, ammo?: number): boolean {
    return this.weapons?.unlockWeapon(weaponId, ammo) ?? false;
  }

  /**
   * Add ammo to a weapon
   */
  addAmmo(weaponId: string, amount: number): boolean {
    return this.weapons?.addAmmo(weaponId, amount) ?? false;
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    // Remove event listeners (check if scene still exists)
    if (this.scene?.events) {
      this.scene.events.off('tractorBeamStarted', this.onTractorBeamStarted, this);
      this.scene.events.off('tractorBeamStopped', this.onTractorBeamStopped, this);
    }

    // Destroy weapon manager
    this.weapons?.destroy();

    super.destroy(fromScene);
  }
}
